<template>
  <div
    class="layer"
    :class="[
      { selected: isSelected },
      { renaming: editing },
      { group: isGroup },
    ]"
    @click="selectElement"
  >
    <template v-if="isGroup">
      <span
        class="layer-type group-arrow"
        :class="getGroupArrowClass"
        data-test="layer-button-collapse"
        @click.stop="toggleCollapse"
      />
    </template>
    <span
      class="layer-type"
      :class="iconClassName"
    />

    <div
      v-show="!editing"
      class="layer-name"
      @dblclick="dblClick"
    >
      {{ element.name }}
    </div>
    <input
      v-show="editing"
      ref="name"
      v-model.lazy="elementName"
      class="layer-name-edit"
      type="text"
      data-test="layer-input-name"
      @blur="closeEditing"
      @keyup.esc="closeEditing"
      @keyup.enter="closeEditing"
      @click.stop
    />

    <div
      v-if="element.type !== 'sheet-data' && element.locked"
      class="btn-lock-layer"
      :class="getLockIcon"
      data-test="layer-button-togglelock"
      @click.stop="elementLocked = !elementLocked"
    />

    <div
      v-if="showPlusButton"
      class="layer-dropdown dropdown layer-dropdown__plus-button"
    >
      <div
        class="dropdown-btn far fa-plus-circle"
        data-toggle="dropdown"
        data-test="layer-button-add"
      />

      <ul class="dropdown-menu">
        <li
          v-for="(el, idx) in validChildTypes"
          :key="`${el.type}-${idx}`"
          class="dropdown-menu-item"
          :data-test="`layer-button-addelement-${el.type}`"
          @click="addElement(el)"
        >
          <span
            :class="el.icon"
            class="margin-right-sm"
          />
          {{ el.name }}
        </li>
      </ul>
    </div>

    <div
      v-if="element.type !== 'marquee-text' && element.type !== 'marquee-image'"
      class="hide-layer-control"
    >
      <label>
        <span
          :class="getHiddenIcon"
        />
        <span class="sr-only">Hide Layer {{ element.type }}</span>
        <input
          v-model="elementLayerHiddenInGraphics"
          class="sr-only"
          type="checkbox"
          data-test="layer-checkbox-hide"
        >
      </label>
    </div>

    <div
      class="layer-dropdown dropdown"
      :class="{'visibility-hidden': !hasLayerOptions}"
    >
      <button
        type="button"
        class="dropdown-btn fa-solid fa-ellipsis-vertical"
        data-test="layer-button-menu"
        @click.stop="openGlobalLayerMenu"
      />
    </div>

    <map-data-modal
      v-if="showMapDataModal"
      :data-provider="dataProvider"
      :type="newElementType"
      :element="element"
      @cancel="showMapDataModal = false"
      @skip="showMapDataModal = false"
      @submit="onSubmitDataMapping"
      @map-many="onMapMany"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import MapDataModal from './Modal/MapDataModal.vue';

import EventBus from '../utils/event-bus';

const getIconClassName = (element) => {
  const { type, collapsed, icon } = element;

  const layerClassNames = {
    timer: 'fas fa-stopwatch',
    slideshow: 'fas fa-presentation',
    group: `fas fa-folder${collapsed ? '' : '-open'}`,
    frame: 'fas fa-window-maximize',
    'sheet-data': 'fas fa-table',
    'data-list': 'fas fa-list',
    rectangle: 'fas fa-square',
    oval: 'fas fa-circle',
    triangle: 'fas fa-triangle',
    date: 'fas fa-clock',
    text: 'fas fa-text',
    qrcode: 'fas fa-qrcode',
    image: 'fas fa-image',
    'marquee-image': 'fas fa-image',
    'marquee-text': 'fas fa-text',
    marquee: 'fak fa-input-text-regular',
    'social-data': 'far fa-phone-laptop',
    'social-display': 'far fa-desktop fa-fw',
    'poll-display': 'far fa-poll-h fa-fw',
    sportradar: 'fak fa-sportradar',
    'multi-media': 'far fa-photo-video',
    shoppable: 'fas fa-cart-plus',
    'linked-data': 'far fa-database',
    video: 'fas fa-video',
  };

  if (icon) {
    return icon;
  }

  return layerClassNames[type] || `icon icon-${type}`;
};

export default {
  emits: ['modal', 'open-layer-menu'],

  components: {
    MapDataModal,
  },
  props: {
    element: {
      type: Object,
      required: true,
    },
    groupId: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      editing: false,
      showMapDataModal: false,
      newElementType: null,
    };
  },

  computed: {
    ...mapGetters('graphicsTemplate', [
      'getElementParentGroupId',
      'getDataProvider',
      'isDataProvider',
    ]),
    isRequired() {
      return this.element.required || this.element.data?.required;
    },
    elementName: {
      get() {
        return this.element.name;
      },
      set(v) {
        const value = {
          id: this.element.id,
          propKey: 'name',
          propValue: v || 'Untitled',
        };
        this.updateElementProp(value);
      },
    },
    elementEditable: {
      get() {
        return this.element.editable;
      },
      set(v) {
        const value = {
          id: this.element.id,
          propKey: 'editable',
          propValue: v,
        };
        this.updateElementProp(value);
      },
    },
    showElementEditableProducer() {
      return this.element.type !== 'group' && this.element.type !== 'slideshow';
    },
    elementDisable: {
      get() {
        return this.element.disable;
      },
      set(v) {
        const value = {
          id: this.element.id,
          propKey: 'disable',
          propValue: v,
        };
        this.updateElementProp(value);
      },
    },
    elementLocked: {
      get() {
        return this.element.locked;
      },
      set(v) {
        const value = {
          id: this.element.id,
          propKey: 'locked',
          propValue: v,
        };
        this.updateElementProp(value);
      },
    },
    elementLayerHiddenInGraphics: {
      get() {
        return this.element?.layerHiddenInGraphics || false;
      },
      set(v) {
        const value = {
          id: this.element.id,
          propKey: 'layerHiddenInGraphics',
          propValue: v,
        };
        this.updateElementProp(value);
      },
    },
    elementCollapsed: {
      get() {
        return this.element.collapsed;
      },
      set(v) {
        const value = {
          id: this.element.id,
          propKey: 'collapsed',
          propValue: v,
        };
        this.updateElementProp(value);
      },
    },
    dataProvider() {
      if (this.isDataProvider(this.element.id)) {
        return this.element;
      }
      return this.getDataProvider(this.element.id);
    },

    // Show plus button only for group type elements
    showPlusButton() {
      const ValidParentElementTypes = [
        'data-list',
        'group',
        'sheet-data',
        'sportradar',
        'social-data',
        'marquee',
        'shoppable',
      ];

      const isValidParentElement = (el) => ValidParentElementTypes.includes(el.type);
      return !this.element.locked && isValidParentElement(this.element);
    },
    hasLayerOptions() {
      return !this.element.noOptionsMenu;
    },

    validChildTypes() {
      let options = [];

      switch (this.element.type) {
        case 'group': {
          options = [
            { name: 'Text', type: 'text' },
            { name: 'Image', type: 'image' },
            { name: 'Rectangle', type: 'rectangle' },
            { name: 'Oval', type: 'oval' },
            { name: 'Triangle', type: 'triangle' },
            { name: 'video', type: 'video' },
          ];

          break;
        }

        case 'social-data': {
          options = [
            { name: 'Text', type: 'text' },
            { name: 'Rectangle', type: 'rectangle' },
            { name: 'Oval', type: 'oval' },
            { name: 'Triangle', type: 'triangle' },
            { name: 'Image', type: 'image' },
            { name: 'Media', type: 'multi-media' },
            { name: 'Group', type: 'group' },
          ];

          break;
        }

        case 'data-list': {
          options = [
            { name: 'Text', type: 'text' },
            { name: 'Image', type: 'image' },
          ];

          break;
        }

        case 'sheet-data': {
          options = [
            { name: 'Dynamic List', type: 'data-list' },
            { name: 'Dynamic Ticker', type: 'marquee' },
          ];

          break;
        }

        case 'sportradar': {
          const providerId = this.dataProvider?.id;

          const hasLogoPermission = providerId
            ? this.$store.getters[`${providerId}/hasLogoPermission`]
            : false;
          const hasHeadshotPermission = providerId
            ? this.$store.getters[`${providerId}/hasHeadshotPermission`]
            : false;

          options = [
            { name: 'Text', type: 'text' },
          ];

          if (hasLogoPermission || hasHeadshotPermission) {
            options.push({ name: 'Image', type: 'image' });
          }

          break;
        }

        case 'marquee': {
          options = [
            { name: 'Ticker Text', type: 'marquee-text' },
            { name: 'Ticker Image', type: 'marquee-image' },
          ];

          break;
        }

        case 'shoppable': {
          options = [
            { name: 'Price', type: 'price-text' },
            { name: 'Name', type: 'name-text' },
            { name: 'Product Image', type: 'product-image' },
          ];

          break;
        }

        default:
          break;
      }

      return options.map((opt) => ({
        ...opt,
        icon: opt.icon || getIconClassName({ type: opt.type }),
      }));
    },

    getGroupIcon() {
      return this.element.collapsed ? 'icon-folder' : 'icon-folder-open';
    },

    getGroupArrowClass() {
      return this.element.collapsed ? 'far fa-angle-right' : 'far fa-angle-down';
    },

    selectedElements() {
      return this.$store.state.templates.selectedElements;
    },

    isSelected() {
      return this.selectedElements.indexOf(this.element.id) !== -1;
    },

    getLockIcon() {
      return this.element.locked ? 'fa-solid fa-lock locked' : 'fa-solid fa-unlock';
    },

    getHiddenIcon() {
      return this.element?.layerHiddenInGraphics ? 'fa-regular fa-eye-slash fa-fw' : 'fa-regular fa-eye fa-fw';
    },

    iconClassName() {
      return getIconClassName(this.element);
    },

    isGroup() {
      switch (this.element.type) {
        case 'group':
        case 'slideshow':
        case 'sheet-data':
        case 'social-data':
        case 'data-list':
        case 'sportradar':
        case 'linked-data':
        case 'marquee':
        case 'shoppable':
          return true;

        default:
          return false;
      }
    },

    allowDuplicate() {
      switch (this.element.type) {
        case 'social-data':
        case 'social-display':
        case 'poll-display':
        case 'shoppable':
          return false;

        case 'qrcode':
          if (this.element.data?.providerType === 'shoppable') {
            return false;
          }
          return true;

        default:
          return true;
      }
    },
  },

  watch: {
    elementLayerHiddenInGraphics(newVal) {
      // re-trigger text resize when hiding/showing text elements
      if (!newVal && this.element.type === 'text') {
        EventBus.emit('resize-text');
      }
    },
  },

  mounted() {
    EventBus.on('layer-menu:action', this.handleLayerMenuAction);
  },

  beforeUnmount() {
    EventBus.off('layer-menu:action', this.handleLayerMenuAction);
  },

  methods: {
    ...mapActions('graphicsTemplate', [
      'setOneElementValueByProp',
      'addNewElement',
      'setManyElementValuesByProp',
      'removeElementAndMappings',
      'takeHistorySnapshot',
      'setElementGroupMapById',
      'unGroupSelectedGroups',
      'whenElementRemovedGroup',
      'duplicateElement',
    ]),
    iconClass(el) {
      return getIconClassName(el);
    },
    updateElementProp(value, options) {
      return this.setOneElementValueByProp({ value, options });
    },
    /**
     * Create child element, but don't bother setting a data mapping
     */
    onSkipDataMapping() {
      this.addChildElement(this.newElementType);
      this.showMapDataModal = false;
    },

    // TODO: DATA THINGS
    onMapMany(elSettings) {
      // do onsubmit for multiple things
      elSettings.forEach((setting) => {
        const { elementType } = setting;
        delete setting.elementType;
        this.onSubmitDataMapping(setting, elementType);
      });
    },

    /**
     * Create a new child element, with default data mapping set to provided column
     */
    async onSubmitDataMapping(settings, type = false) {
      this.showMapDataModal = false;

      // add data-list children "backwards"
      const position = this.element.type === 'data-list' ? 'end' : undefined;

      const newType = type || this.newElementType;

      if (settings.syncLabel) {
        // eslint-disable-next-line no-param-reassign
        delete settings.syncLabel;

        // const { name, toTop } = settings;
        const { dataKeyMap, provider } = settings.data;

        const linkedGroupElement = await this.addNewElement({
          value: {
            type: 'linked-data',
            parentId: this.element.id,
            name: settings.name,
            position,
          },
        });

        // create group
        await this.setElementGroupMapById({
          value: {
            id: linkedGroupElement.id,
            childIds: [],
          },
        });

        const addedElement = await this.addNewElement({
          value: {
            type: newType,
            parentId: linkedGroupElement.id,
            settings: {
              ...settings,
              name: `${settings.name} Value`,
            },
          },
        });

        const { top, left, height } = addedElement.styles;

        const labelSettings = {
          type: 'text',
          parentId: linkedGroupElement.id,
          settings: {
            name: `${settings.name} Label`,
            data: {
              dataKeyMap,
              isLabel: true,
              provider,
              linkedElement: addedElement.id,
            },
            styles: {
              top: top + height,
              left,
            },
          },
        };

        const labelElement = await this.addNewElement({
          value: labelSettings,
        });

        // update "value" element with linked label info
        this.setManyElementValuesByProp({
          value: {
            id: addedElement.id,
            pathValueMap: {
              'data.linkedElement': labelElement.id,
            },
          },
        });
      } else {
        await this.addNewElement({
          value: {
            type: newType,
            settings,
            parentId: this.element.id,
            position,
          },
        });
      }
    },

    async duplicate() {
      await this.duplicateElement({
        value: this.element.id,
      });
      // TODO: jQuery bootstrap icky - needs that selector to be a jquery selector to work.
      $(this.$el).find('[data-toggle="dropdown"]').dropdown('toggle');
    },

    async removeElement() {
      await this.removeElementAndMappings({
        value: {
          groupKey: this.groupId,
          element: this.element,
        },
        options: {
          // don't record to history because of multiple levels of mutation
          // in the same action
          blacklist: true,
        },
      });
      // may need some more tweaks for group/element size/position calc
      await this.whenElementRemovedGroup({
        value: {
          groupId: this.groupId,
        },
        options: {
          // don't record to history because of multiple levels of mutation
          // in the same action
          blacklist: true,
        },
      });
      // instead wait until the full removal is completed, then take a history snapshot
      this.takeHistorySnapshot();

      // TODO: jQuery bootstrap icky - needs that selector to be a jquery selector to work.
      $(this.$el).find('[data-toggle="dropdown"]').dropdown('toggle');
    },

    renameElement() {
      this.editing = true;
      this.$nextTick(() => {
        this.$refs.name.focus();
        this.$refs.name.select();
        // TODO: jQuery bootstrap icky - needs that selector to be a jquery selector to work.
        $(this.$el).find('[data-toggle="dropdown"]').dropdown('toggle');
      });
    },

    selectElement(event) {
      if (this.element.type === 'group') {
        this.$store.dispatch('unselectGroup', this.element.id);
      }
      if (event.shiftKey) {
        this.$store.dispatch('updateSelectedElements', this.element.id);
      } else {
        this.$store.dispatch('selectElements', [this.element.id]);
      }
    },
    toggleDisable() {
      this.elementDisable = !this.elementDisable;
    },
    dblClick() {
      if (!this.element) {
        return;
      }

      this.editing = true;

      this.$nextTick(() => {
        this.$refs.name.focus();
        this.$refs.name.select();
      });
    },

    unGroup() {
      this.$store.dispatch('selectElements', [this.element.id]);
      this.unGroupSelectedGroups();
    },

    toggleCollapse() {
      this.elementCollapsed = !this.elementCollapsed;
    },

    /**
     * Add a plain ole child element with no data mapping
     * @param {string} type - Type of element to add
     */
    async addChildElement(type) {
      // add data-list stuff "backwards"
      const addToEnd = ['data-list', 'marquee-text', 'marquee-image'].includes(type);
      const position = addToEnd ? 'end' : undefined;

      if (this.element.type === 'shoppable') {
        if (type === 'price-text') {
          const name = 'Price';
          await this.addNewElement({
            value: {
              type: 'text',
              settings: {
                name,
                styles: {
                  top: 0,
                  left: 0,
                },
                data: {
                  provider: this.element.id,
                  providerType: 'shoppable',
                  dataKeyMap: {
                    text: 'productPrice',
                  },
                },
              },
              parentId: this.element.id,
              position,
              isGroup: false,
            },
          });

          return;
        }

        if (type === 'name-text') {
          const name = 'Name';
          await this.addNewElement({
            value: {
              type: 'text',
              settings: {
                name,
                styles: {
                  top: 0,
                  left: 0,
                },
                data: {
                  provider: this.element.id,
                  providerType: 'shoppable',
                  dataKeyMap: {
                    text: 'productName',
                  },
                },
              },
              parentId: this.element.id,
              position,
              isGroup: false,
            },
          });

          return;
        }

        if (type === 'product-image') {
          const name = 'Product Image';
          await this.addNewElement({
            value: {
              type: 'image',
              settings: {
                name,
                data: {
                  provider: this.element.id,
                  providerType: 'shoppable',
                  dataKeyMap: {
                    imageUrl: 'productImage',
                  },
                },
              },
              parentId: this.element.id,
              position,
              isGroup: false,
            },
          });

          return;
        }
      }

      if (this.element.type === 'sheet-data' && type === 'marquee') {
        await this.addNewElement({
          value: {
            type,
            settings: {
              name: 'Dynamic Ticker',
              data: {
                provider: this.element.id,
                providerType: 'sheet-data',
                range: '',
                filters: [{
                  type: 'pagination',
                  offset: 1, // Start from item... (index + 1)
                  page: 1, // Page number (index + 1)
                  limit: 10, // Items per page
                }],
              },
            },
            parentId: this.element.id,
            isGroup: true,
          },
        });

        return;
      }
      // Otherwise, just create the new element
      if (this.element.type === 'social-data' && type === 'multi-media') {
        // multi-media type is currently only supported in social and
        // has only one mapping option, so set it's mapping up because there
        // is no other way to use this one right now
        await this.addNewElement({
          value: {
            type,
            settings: {
              editable: false,
              backgroundColor: 'transparent',
              fixedParent: true,
              data: {
                provider: this.element.id,
                dataKeyMap: {
                  src: 'media',
                  isVideo: 'media',
                  poster: 'media',
                },
              },
              aspectLock: false,
            },
            parentId: this.element.id,
            position,
            isGroup: type === 'group',
          },
        });
        return;
      }

      if (type === 'image') {
        this.$emit('modal', { open: true, type });

        window.assetmanfn = ({ asset, meta: additiveMeta = {} }) => {
          const settings = {
            name: asset?.title,
            styles: {
              left: 0,
              top: 0,
            },
          };

          if (asset?.asset_type === 'video') {
            settings.src = asset.media_url;
            settings.thumbnailUrl = asset.thumbnail_url;
            settings.posterUrl = asset.thumbnail_url;
            settings.styles.width = asset.meta.width;
            settings.styles.height = asset.meta.height;
            settings.duration = asset.meta.duration;
            settings.contentType = asset.meta.content_type;
          } else if (asset?.asset_type === 'image') {
            settings.imageUrl = asset.media_url;
            settings.styles.width = asset.meta.width;
            settings.styles.height = asset.meta.height;
            settings.imageMeta = additiveMeta;
          }

          this.addNewElement({
            value: {
              // NOTE: Asset records created before December 21, 2021
              // currently do not have an `asset_type` property.
              // This fallback is intended to be a temporary fix until the data can be backfilled.
              type: asset.asset_type || 'image',
              settings,
              parentId: this.element.id,
            },
          });

          this.$emit('modal', { open: false });
        };
      } else {
        // Just add the plain ole child element
        const addedEl = await this.addNewElement({
          value: {
            type,
            settings: {
              styles: {
                top: 0,
                left: 0,
              },
            },
            parentId: this.element.id,
            position,
            isGroup: type === 'group',
          },
        });

        // add group map for elements that need it
        if (['data-list'].includes(type)) {
          this.setElementGroupMapById({
            value: {
              id: addedEl.id,
              childIds: [],
            },
          });
        }
      }
    },

    /**
     * Add new child element to current element as child.
     * If this element is a group, just use a normal element.
     * Otherwise it's a data-mapped element.
     * @param {string} type - The type of child element to create
     */
    async addElement(el) {
      const { type } = el;
      if (
        ['data-list', 'sportradar'].includes(this.element.type)
        && this.dataProvider
      ) {
        this.newElementType = type;
        this.showMapDataModal = true;
        return;
      }

      this.addChildElement(type);
    },
    closeEditing() {
      this.editing = false;
    },
    openGlobalLayerMenu(event) {
      const rect = event.currentTarget.getBoundingClientRect();

      this.$emit('open-layer-menu', {
        id: this.element.id,
        rect,
        canUnGroup: this.element.type === 'group',
        canToggleLock: this.element.type !== 'sheet-data',
        canToggleEditable: this.showElementEditableProducer,
        canDuplicate: this.allowDuplicate,
        canDelete: !this.isRequired,
        canRefresh: this.element.type === 'frame',
        isLocked: this.element.locked,
        isEditable: this.elementEditable,
      });
    },

    handleLayerMenuAction({ id, action }) {
      if (id !== this.element.id) return;

      switch (action) {
        case 'ungroup':
          if (this.element.type === 'group') this.unGroup();
          break;

        case 'toggle-lock':
          if (this.element.type !== 'sheet-data') {
            this.elementLocked = !this.elementLocked;
          }
          break;

        case 'toggle-editable':
          if (this.showElementEditableProducer) {
            this.elementEditable = !this.elementEditable;
          }
          break;

        case 'refresh':
          if (this.element.type === 'frame') this.refreshFrame();
          break;

        case 'rename':
          this.renameElement();
          break;

        case 'duplicate':
          if (this.allowDuplicate) this.duplicate();
          break;

        case 'delete':
          if (!this.isRequired) this.removeElement();
          break;

        default:
          break;
      }
    },

    refreshFrame() {
      EventBus.emit('frame:refresh', { id: this.element.id });
    },
  },
};
</script>
